/**
 * Find matching drafts across countries by static tag name.
 * Used for "Apply to matching drafts in other countries?" propagation.
 */

import { useMemo } from 'react';

import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';
import { isScheduledSentPrepared } from '@/features/smswiser-campaigns/components/MultiCountrySentScheduledEdit/shared';

import { buildBulkSourceCountryGroup } from './buildBulkSourceCountryGroup';
import type { StaticTagPropagationMatchKind } from './staticTagPropagation.types';

export type { StaticTagPropagationMatchKind } from './staticTagPropagation.types';

export interface StaticTagMatch {
  countryKey: string;
  tagName: string;
  matchingIds: string[];
}

export interface StaticTagCountryGroup {
  countryKey: string;
  tags: Array<{ tagName: string; draftCount: number }>;
  totalDrafts: number;
  /** Bulk-edit “home” country row — always included; never added via propagation. */
  isBulkSourceCountry?: boolean;
}

export interface UseStaticTagPropagationParams {
  selectedIds: string[];
  allDrops: PreparedDrop[];
  sourceCountryKey: string;
  /** Default: drafts only. `scheduled_sent` matches future scheduled SENT prepared rows. */
  matchKind?: StaticTagPropagationMatchKind;
}

function matchesPropagationKind(d: PreparedDrop, kind: StaticTagPropagationMatchKind): boolean {
  if (kind === 'scheduled_sent') return isScheduledSentPrepared(d);
  return d.status === 'DRAFT';
}

export function useStaticTagPropagation(params: UseStaticTagPropagationParams) {
  const { selectedIds, allDrops, sourceCountryKey, matchKind = 'draft' } = params;

  const selectedTagNames = useMemo(() => {
    const sel = new Set(selectedIds);
    const tags = new Set<string>();
    for (const d of allDrops) {
      if (sel.has(d.id) && d.staticTag?.name) tags.add(d.staticTag.name);
    }
    return tags;
  }, [selectedIds, allDrops]);

  const matchesByCountry = useMemo((): StaticTagMatch[] => {
    if (selectedTagNames.size === 0) return [];
    const result: StaticTagMatch[] = [];
    const byCountry = new Map<string, Map<string, string[]>>();

    for (const d of allDrops) {
      if (!matchesPropagationKind(d, matchKind) || d.countryKey === sourceCountryKey) continue;
      if (!d.staticTag?.name || !selectedTagNames.has(d.staticTag.name)) continue;
      const countryMap = byCountry.get(d.countryKey) ?? new Map<string, string[]>();
      const ids = countryMap.get(d.staticTag.name) ?? [];
      ids.push(d.id);
      countryMap.set(d.staticTag.name, ids);
      byCountry.set(d.countryKey, countryMap);
    }

    for (const [ck, tagMap] of byCountry) {
      for (const [tagName, ids] of tagMap) {
        result.push({ countryKey: ck, tagName, matchingIds: ids });
      }
    }

    return result.sort((a, b) => a.countryKey.localeCompare(b.countryKey));
  }, [allDrops, sourceCountryKey, selectedTagNames, matchKind]);

  const countryGroupsFromMatches = useMemo((): StaticTagCountryGroup[] => {
    const map = new Map<string, StaticTagCountryGroup>();
    for (const m of matchesByCountry) {
      const prev = map.get(m.countryKey);
      const tagEntry = { tagName: m.tagName, draftCount: m.matchingIds.length };
      if (!prev) {
        map.set(m.countryKey, {
          countryKey: m.countryKey,
          tags: [tagEntry],
          totalDrafts: m.matchingIds.length,
        });
      } else {
        prev.tags.push(tagEntry);
        prev.totalDrafts += m.matchingIds.length;
        map.set(m.countryKey, prev);
      }
    }
    return [...map.values()].sort((a, b) => a.countryKey.localeCompare(b.countryKey));
  }, [matchesByCountry]);

  const countryGroups = useMemo((): StaticTagCountryGroup[] => {
    if (matchesByCountry.length === 0) return countryGroupsFromMatches;
    const src = buildBulkSourceCountryGroup({
      allDrops,
      selectedIds,
      sourceCountryKey,
      selectedTagNames,
      matchKind,
    });
    if (!src) return countryGroupsFromMatches;
    return [src, ...countryGroupsFromMatches.filter((g) => g.countryKey !== src.countryKey)];
  }, [matchesByCountry, countryGroupsFromMatches, allDrops, selectedIds, sourceCountryKey, selectedTagNames, matchKind]);

  const totalMatchCount = useMemo(
    () => matchesByCountry.reduce((sum, m) => sum + m.matchingIds.length, 0),
    [matchesByCountry],
  );

  const allMatchingIds = useMemo(
    () => matchesByCountry.flatMap((m) => m.matchingIds),
    [matchesByCountry],
  );

  const staticTagColorByName = useMemo(() => {
    const map: Record<string, string> = {};
    for (const d of allDrops) {
      const name = d.staticTag?.name;
      const color = d.staticTag?.color;
      if (name && color) map[name] = color;
    }
    return map;
  }, [allDrops]);

  return {
    selectedTagNames,
    matchesByCountry,
    countryGroups,
    totalMatchCount,
    allMatchingIds,
    staticTagColorByName,
    hasMatches: matchesByCountry.length > 0,
  };
}
